import { COMPILED_COLLISION_CELLS_PER_TILE } from "../document/types.ts";
import { TIE_STEP } from "../editor/sort.ts";
import type { ChunkStand } from "./stand-chunk.ts";
import { STAND_BEHIND, STAND_FRONT, type StandLookup, type StandTable } from "./stand-marks.ts";

/**
 * How a character is drawn against a painted prop (ADR-132 decisions 3, 4 and 6): the depth rule the game and the
 * editor's stand-in share, and the map-wide stand table the game assembles from its chunks.
 */

/**
 * The distance a character's depth sits from the prop it is drawn behind or in front of: half a tie step. It is
 * under the step between two props with neighbouring tie keys, so a character stays inside its prop's slot, and over
 * one unit in the last place of a depth in any band the sort supports, so it is never lost to rounding.
 */
export const STAND_HAIR = TIE_STEP / 2;

/**
 * The depth a character takes on a marked cell.
 *
 * @param side `STAND_BEHIND`, `STAND_FRONT`, or anything else for an unmarked cell.
 * @param propDepth The depth of the prop that decides the cell.
 * @param own The depth the character's own feet row gives it, kept where nothing is marked.
 * @returns Just under the prop's depth on a Behind cell, just over it on an In front cell, else `own`.
 */
export function standDepth(side: number, propDepth: number, own: number): number {
  if (side === STAND_BEHIND) {
    return propDepth - STAND_HAIR;
  }
  return side === STAND_FRONT ? propDepth + STAND_HAIR : own;
}

/**
 * The depth of a character at a feet point (ADR-132 decision 4). The cell under the feet is the compiled collision
 * cell that holds the pixel row just above the feet line, as the occluder fade's body box does (`y - 1`), so a feet
 * line on a cell's lower edge belongs to that cell. Allocates nothing.
 *
 * @param lookup The map's marks, or null where none are known.
 * @param feetX Feet x in world pixels.
 * @param feetY Feet line in world pixels.
 * @param tileSize Tile size in pixels.
 * @param own The depth the character's own feet row gives it.
 * @returns The depth the character draws at.
 */
export function standDepthAt(lookup: StandLookup | null, feetX: number, feetY: number, tileSize: number, own: number): number {
  if (lookup === null) {
    return own;
  }
  const px = tileSize / COMPILED_COLLISION_CELLS_PER_TILE;
  const cx = Math.floor(feetX / px);
  const cy = Math.floor((feetY - 1) / px);
  const side = lookup.sideAt(cx, cy);
  return side === 0 ? own : standDepth(side, lookup.depthAt(cx, cy), own);
}

/**
 * The map-wide stand table of a map's loaded chunks, the inverse of `chunkStands`.
 *
 * @param chunks The chunks that carry a stand table, with their place in the chunk grid.
 * @param chunkSize Chunk size in tiles.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @returns The table, sorted by cell index.
 */
export function standTableOfChunks(chunks: Iterable<{ readonly cx: number; readonly cy: number; readonly stand: ChunkStand }>, chunkSize: number, width: number, height: number): StandTable {
  const span = chunkSize * COMPILED_COLLISION_CELLS_PER_TILE;
  const cellsWide = width * COMPILED_COLLISION_CELLS_PER_TILE;
  const entries: { key: number; side: number; depth: number }[] = [];
  for (const { cx, cy, stand } of chunks) {
    for (let i = 0; i < stand.cells.length; i += 1) {
      const cell = stand.cells[i] as number;
      const column = cx * span + (cell % span);
      const row = cy * span + Math.floor(cell / span);
      entries.push({ key: row * cellsWide + column, side: stand.sides[i] as number, depth: stand.depths[i] as number });
    }
  }
  entries.sort((a, b) => a.key - b.key);
  return {
    width: cellsWide,
    height: height * COMPILED_COLLISION_CELLS_PER_TILE,
    keys: Int32Array.from(entries, (entry) => entry.key),
    sides: Uint8Array.from(entries, (entry) => entry.side),
    depths: Float64Array.from(entries, (entry) => entry.depth),
  };
}
